import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { forkJoin, from, of, Subject } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take, takeUntil, tap } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CommunityVisitService } from "../community-visit.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { OnboardingCentredModalComponent } from "@common/onboarding-centred-modal/onboarding-centred-modal.component";
import { isOnRoute, openOnboardingCentredModalWithRef, whenUiQuiet } from "./onboarding-lesson-modal";

/** The Community page - CM01 only ever shows here. */
export const COMMUNITY_ROUTE = "/community";

export const CM01D_LESSON_ID = "CM01D";
export const CM01M_LESSON_ID = "CM01M";

/** Recorded with either lesson's completed state - both write the same milestone. */
export const COMMUNITY_ORIENTATION_VIEWED = "community_orientation_viewed";

/** The Community item of the side nav (`flipCoachTarget` in side-nav.component.html). */
export const CM01D_ANCHOR = "community.desktop_navigation";

/** The Community item of the bottom nav (`flipCoachTarget` in bottom-nav.component.html). */
export const CM01M_ANCHOR = "community.mobile_menu";

/**
 * Copy rows CM01D.01-CM01D.02 and CM01M.01-CM01M.02, workbook v2.38 Copy sheet (verbatim). Both
 * rows are still flagged "Needs confirmation" - the wording is expected to change, so it lives
 * here and nowhere else.
 */
export const CM01D_COPY = {
  heading: "Welcome to Community",
  body:
    "Community is a place to share experiences, find practical support and connect with people who " +
    "understand. Explore different spaces using the menu on the left. You can browse without posting, " +
    "or introduce yourself whenever you feel ready.",
};

export const CM01M_COPY = {
  heading: "Welcome to Community",
  body:
    "Community is a place to share experiences, find practical support and connect with people who " +
    "understand. You'll find different spaces in the Community menu. You can browse without posting, " +
    "or introduce yourself whenever you feel ready.",
};

const CM01_LESSON_IDS: readonly string[] = [CM01D_LESSON_ID, CM01M_LESSON_ID];

/**
 * Onboarding lessons CM01D / CM01M "Welcome to Community" (FLIP-387 / FLIP-394, story FLIP-383;
 * spec v2.36 §21.1, workbook v2.38, release switch `community`). One service for both, because the
 * rules are identical - only the body copy differs:
 *
 * - Informational centred modal without buttons (OnboardingCentredModalComponent; was an anchored
 *   prompt at the nav's Community item, so `CM01D_ANCHOR` / `CM01M_ANCHOR` now only register those
 *   nav items), shown on a Community visit while the `onboarding-walkthrough` flag is on, once no
 *   blocking product modal/snackbar is open (`blocking_modal_active` false) and the user is still on
 *   /community. It never navigates and never opens Introduce Yourself.
 * - Exactly one of the two shows: CM01D (copy pointing at the menu on the left) when the side nav is
 *   on screen, otherwise CM01M (the Community menu). Which nav is displayed is a CSS decision
 *   (breakpoints and touch-tablet rules), so it is read from the DOM rather than duplicated as a
 *   media query.
 * - The visible close completes it permanently. `onTargetUsed` (the Community nav item) still
 *   completes it if it fires, though the modal blocks the page until closed. Either lesson
 *   completing resolves both, and both record
 *   `community_orientation_viewed` - as with H01, CompletedAt on the lesson's UserOnboardingStep row
 *   *is* the milestone; its key is named in the tracking detail.
 * - Leaving Community without dismissing closes the prompt and leaves it unresolved, so it returns on
 *   the next visit.
 * - Existing users: suppressed if the account already has the `HasVisitedCommunity` flag (FLIP-323)
 *   - unless a CM01 row was already started, which proves the flag is this lesson's own first visit
 *   and not an earlier one. The flag is read here, *before* it is written: `markVisited()` is called
 *   from this service once the read settles (only if it wasn't already set), so the first visit can't
 *   suppress itself. A failed read holds the lesson back and skips the write, so the next visit
 *   retries rather than guessing.
 */
@Injectable({ providedIn: "root" })
export class CM01CommunityWelcomeService {
  private readonly leave$ = new Subject<void>();
  private completedInSession = false;
  private promptOpen = false;
  private presentedLessonId: string | null = null;
  private modalRef: MatDialogRef<OnboardingCentredModalComponent> | null = null;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private blockingUiSignal: BlockingUiSignalService,
    private featureFlagService: FeatureFlagService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private communityVisitService: CommunityVisitService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - drop the entry and this session's state so nothing carries over.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.leave();
      this.completedInSession = false;
    });
  }

  /** Call on each arrival at Community. Also records the account's first visit (FLIP-323). */
  enter(): void {
    this.leave();

    // Read before write - see the class comment. Runs whether or not the lesson is eligible, so the
    // visit is recorded either way; deliberately not tied to leave$.
    const priorVisit$ = this.communityVisitService.hasVisited().pipe(
      take(1),
      map((visited): boolean | undefined => visited),
      catchError(() => of(undefined)),
      tap((visited) => {
        if (visited === false) {
          this.communityVisitService.markVisited();
        }
      }),
      shareReplay(1)
    );
    priorVisit$.subscribe();

    if (this.completedInSession) {
      return;
    }

    this.paymentsService.subscriptionActive$
      .pipe(
        filter((active) => active),
        take(1),
        switchMap(() => from(this.featureFlagService.init())),
        filter(() => this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)),
        switchMap(() => forkJoin([this.onboardingStepService.getSteps(), priorVisit$])),
        // undefined = the read failed; hold back rather than guess.
        filter(([steps, visited]) => steps !== undefined && visited !== undefined),
        filter(([steps, visited]) => this.isEligible(steps ?? [], visited === true)),
        tap(() => this.recordStarted(this.lessonForViewport())),
        switchMap(() => whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$)),
        filter(() => !this.completedInSession && isOnRoute(this.router, COMMUNITY_ROUTE)),
        take(1),
        takeUntil(this.leave$)
      )
      .subscribe(() => this.open());
  }

  /** Call when leaving Community - closes the prompt, records nothing; it shows again next visit. */
  leave(): void {
    this.leave$.next();
    this.closePrompt();
  }

  /** Call when the user uses the Community nav item - completes the lesson if its prompt is up. */
  onTargetUsed(): void {
    if (this.promptOpen && this.presentedLessonId) {
      this.closePrompt();
      this.complete(this.presentedLessonId, "nav_used");
    }
  }

  /** Closes the modal without an exit - clears promptOpen first so its close handler records nothing. */
  private closePrompt(): void {
    if (this.promptOpen) {
      this.promptOpen = false;
      this.modalRef?.close();
      this.modalRef = null;
    }
  }

  private isEligible(steps: { stepKey: string; startedAt?: string | null; completedAt?: string | null; dismissed: boolean }[], visited: boolean): boolean {
    const rows = steps.filter((step) => CM01_LESSON_IDS.includes(step.stepKey));
    if (rows.some((row) => row.completedAt || row.dismissed)) {
      return false;
    }
    // Already known to have visited Community - unless this lesson itself was started on that visit.
    return !visited || rows.some((row) => !!row.startedAt);
  }

  /** CM01D when the side nav is on screen, otherwise CM01M (the bottom nav). */
  private lessonForViewport(): string {
    const sideNavItem = document.querySelector<HTMLElement>(`[data-flip-coach-target="${CM01D_ANCHOR}"]`);
    return sideNavItem && sideNavItem.getClientRects().length > 0 ? CM01D_LESSON_ID : CM01M_LESSON_ID;
  }

  /** Marks the lesson started as soon as it is found eligible, so a visit that never gets to show it still owns it. */
  private recordStarted(lessonId: string): void {
    this.onboardingStepService.upsertStep({ stepKey: lessonId, startedAt: new Date() });
  }

  private open(): void {
    const lessonId = this.lessonForViewport();
    const copy = lessonId === CM01D_LESSON_ID ? CM01D_COPY : CM01M_COPY;
    this.promptOpen = true;
    this.presentedLessonId = lessonId;
    this.onboardingStepService.upsertStep({ stepKey: lessonId, lastViewedAt: new Date() });

    // No engine tracks a centred modal, so started / presented and the close action are recorded here,
    // in the engine's shape. No buttons: any exit (close X, Escape, the dialog closing on navigation)
    // is the standard dismissal and completes the lesson.
    this.track(lessonId, "onboarding_lesson_state_changed", "started");
    const { ref, exit$ } = openOnboardingCentredModalWithRef(this.dialog, { heading: copy.heading, body: copy.body });
    this.modalRef = ref;
    this.track(lessonId, "onboarding_lesson_presented");
    exit$.subscribe(() => {
      this.modalRef = null;
      if (!this.promptOpen) {
        return; // closed by closePrompt() - leaving Community, or the nav item used
      }
      this.promptOpen = false;
      this.complete(lessonId, "dismiss");
    });
  }

  /** Completes the lesson permanently. `action` is tracked unless null (the engine already did). */
  private complete(lessonId: string, action: string | null): void {
    this.completedInSession = true;
    this.leave$.next();
    this.onboardingStepService.upsertStep({ stepKey: lessonId, completedAt: new Date() });
    if (action) {
      this.track(lessonId, "onboarding_lesson_action", `action: ${action}`);
    }
    this.track(lessonId, "onboarding_lesson_state_changed", `completed; milestone: ${COMMUNITY_ORIENTATION_VIEWED}`);
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(lessonId: string, eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${lessonId}; ${detail}` : `lesson: ${lessonId}`,
    });
  }
}
